'use client';

import * as React from 'react';
import { Plus } from 'lucide-react';
import { cn } from '../utils/cn';
import { Tooltip } from './tooltip';
import { ChatSwitcher } from './chat-switcher';
import { getKeyDisplayText, getModifierKey } from '../utils/platform';

export type SidebarHeaderProps = {
  showArchived: boolean;
  onToggleArchived: (showArchived: boolean) => void;
  onNewChat: () => void;
  className?: string;
};

/**
 * SidebarHeader component
 *
 * Header for the chat sidebar containing:
 * - New chat button (+) on the left
 * - Chats/Archived switcher on the right
 *
 * Inspired by Claude's sidebar design
 */
export const SidebarHeader = React.forwardRef<HTMLDivElement, SidebarHeaderProps>(
  ({ showArchived, onToggleArchived, onNewChat, className }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('flex items-center justify-between gap-2 px-3 py-2', className)}
        data-testid="sidebar-header"
      >
        {/* Right: Chat/Archived Switcher */}
        <ChatSwitcher showArchived={showArchived} onToggle={onToggleArchived} />

        {/* Right: New Chat Button */}
        <Tooltip
          content={`New Chat ${getKeyDisplayText(getModifierKey())}+K`}
          side="left"
          delay={300}
        >
          <button
            type="button"
            onClick={onNewChat}
            className={cn(
              'h-7 w-7 flex items-center justify-center rounded-md flex-shrink-0',
              'text-muted-foreground hover:text-foreground',
              'hover:bg-accent/50 active:scale-95',
              'transition-all duration-200'
            )}
            aria-label="New chat"
            data-testid="sidebar-new-chat"
          >
            <Plus className="w-4 h-4" />
          </button>
        </Tooltip>
      </div>
    );
  }
);

SidebarHeader.displayName = 'SidebarHeader';
